/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { getLogger } from '../../utils/SearchLogger';
import SearchHostContext from '../../utils/SearchHostContext';

export const leoCompletionRequest = async ({
	searchQuery,
	searchHostContext,
}: {
	searchQuery: string;
	searchConfig: any;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		let sanitizeInput = searchQuery;

		if (typeof String.prototype.normalize === 'function') {
			// normalize() exists, use it
			sanitizeInput = sanitizeInput.normalize();
		}

		sanitizeInput = sanitizeInput.trim().toLocaleLowerCase();
		const { LeoApiClient } = await import(/* webpackChunkName: "leo" */ '@leo/leo-ui');
		const leoResponse = await LeoApiClient.getAutoSuggestions({
			query: sanitizeInput,
			accessToken: searchHostContext.imsInfo.accessToken,
			xApiKey: searchHostContext.hostInfo.apiKey,
			environment: searchHostContext.environment,
			skipAnalytics: SearchHostContext.getOptOutAnalytics(),
		});

		let processedLeoResponse: any[] = [];
		if (leoResponse) {
			processedLeoResponse = leoResponse?.suggestions.map((suggestionString) => ({
				name: suggestionString,
				source: 'Leo Autosuggestion Dropdown',
			}));
		}
		return processedLeoResponse;
	} catch (e) {
		getLogger().error('Leo Autocomplete Service', 'Leo Autocomplete Service connect error', e.toString());
		const autocompleteError = new Error('Leo Autocomplete Service connect error');
		return autocompleteError;
	}
};
